/**
 * Error handling utilities.
 * Forked from XanBZS: packages/backend/src/errors.ts
 */

import { toast } from "sonner";

import { logger } from "@/lib/logging";

export class QueryError extends Error {
  code?: string;
  details?: string;
  hint?: string;

  constructor(message: string, options?: { code?: string; details?: string; hint?: string }) {
    super(message);
    this.name = "QueryError";
    this.code = options?.code;
    this.details = options?.details;
    this.hint = options?.hint;
  }
}

export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes("NetworkError") ||
      error.message.includes("Failed to fetch") ||
      error.message.includes("Load failed"))
  );
}

/**
 * Records a failure for operators without surfacing its raw text.
 *
 * Backend and driver messages can carry query, schema, or infrastructure
 * detail (CWE-209), so screens render curated copy while the original error
 * stays in the structured log.
 */
export function reportSuppressedError(context: string, error: unknown): void {
  logger.error(
    {
      context,
      detail: error instanceof Error ? error.stack || error.message : error,
    },
    `${context} failed`,
  );
}

/**
 * Tells the operator a write failed, using curated copy.
 *
 * A failed mutation must never fail silently, but it must also not echo the
 * backend's text: rendering `error.message` verbatim leaks the detail
 * `reportSuppressedError` exists to keep out of the UI, and an `Error("")`
 * renders an empty toast with no visible failure at all. The raw error still
 * reaches the structured log.
 */
export function reportMutationError(
  context: string,
  error: unknown,
  operatorMessage: string,
): void {
  reportSuppressedError(context, error);
  toast.error(operatorMessage);
}
